/**
 * Building-specific lighting zone configurations
 * Maps building IDs to their lighting zone layouts
 *
 * This configuration allows different buildings to have different lighting
 * zone segmentation and component counts. For example, Rocky Hill has a
 * 3-zone Spa Suite with RGBW lighting, while New London/Cheshire have
 * a 2-zone Spa Suite with UVC.
 */

export interface LightingZone {
  zoneId: string;           // e.g., "livingspace_a", "hallway_a", "bedroom_a"
  displayName: string;      // e.g., "Living Room", "Spa Suite", "Bedroom"
  segmentCount: number;     // 1, 2, or 3 for multi-segment zones
}

export interface BuildingLightingConfig {
  buildingId: string;
  buildingName: string;
  zones: LightingZone[];
}

/**
 * Default lighting configuration used by New London and Cheshire buildings
 * - 2 Living Room lights
 * - 2 Spa Suite lights (including bathroom/vanity/shower)
 * - 1 UVC light
 * - 2 Bedroom lights
 */
const DEFAULT_LIGHTING_CONFIG: BuildingLightingConfig = {
  buildingId: 'default',
  buildingName: 'New London / Cheshire',
  zones: [
    {
      zoneId: 'livingspace_a',
      displayName: 'Living Room',
      segmentCount: 2
    },
    {
      zoneId: 'hallway_a',
      displayName: 'Spa Suite',
      segmentCount: 2
    },
    {
      zoneId: 'bedroom_a',
      displayName: 'Bedroom',
      segmentCount: 2
    }
  ]
};

/**
 * Rocky Hill lighting configuration
 * - 2 Living Room lights (includes kitchen RGBW on existing zone)
 * - 3 Spa Suite lights (RGBW replaces UVC)
 * - 2 Bedroom lights
 *
 * Key differences from default:
 * - Spa Suite has 3 segments instead of 2 (hallway_a_light_c added for kitchen RGBW)
 * - No UVC light entity
 * - Same zone IDs used (zones interpret entities differently per building)
 */
const ROCKY_HILL_LIGHTING_CONFIG: BuildingLightingConfig = {
  buildingId: 'rocky-hill',
  buildingName: 'Rocky Hill',
  zones: [
    {
      zoneId: 'livingspace_a',
      displayName: 'Living Room',
      segmentCount: 2
    },
    {
      zoneId: 'hallway_a',
      displayName: 'Spa Suite',
      segmentCount: 3  // Includes kitchen RGBW
    },
    {
      zoneId: 'bedroom_a',
      displayName: 'Bedroom',
      segmentCount: 2
    }
  ]
};

/**
 * Mapping of all building lighting configurations
 * Key: buildingId (from Building.id in GraphQL)
 * Value: BuildingLightingConfig with zones and segment counts
 */
export const BUILDING_LIGHTING_CONFIGS: { [buildingId: string]: BuildingLightingConfig } = {
  'default': DEFAULT_LIGHTING_CONFIG,
  'rocky-hill': ROCKY_HILL_LIGHTING_CONFIG
};

/**
 * Get lighting configuration for a specific building
 * Falls back to default config if building not found
 *
 * @param buildingId - The building ID to look up
 * @returns BuildingLightingConfig for the building
 */
export function getLightingConfigForBuilding(buildingId: string | undefined): BuildingLightingConfig {
  if (!buildingId) {
    return DEFAULT_LIGHTING_CONFIG;
  }

  return BUILDING_LIGHTING_CONFIGS[buildingId] || DEFAULT_LIGHTING_CONFIG;
}

/**
 * Get the zone display names for a specific building
 * Useful for mapping zoneIds to friendly UI labels
 *
 * @param buildingId - The building ID
 * @returns Map of zoneId -> displayName
 */
export function getZoneDisplayNames(buildingId: string | undefined): { [zoneId: string]: string } {
  const config = getLightingConfigForBuilding(buildingId);
  const names: { [zoneId: string]: string } = {};

  config.zones.forEach(zone => {
    names[zone.zoneId] = zone.displayName;
  });

  return names;
}

/**
 * Get device IDs (zoneIds) for a specific building
 * These are the zone identifiers used throughout the system
 *
 * @param buildingId - The building ID
 * @returns Array of zone IDs for this building's lights
 */
export function getBuildingLightDevices(buildingId: string | undefined): string[] {
  const config = getLightingConfigForBuilding(buildingId);
  return config.zones.map(zone => zone.zoneId);
}
